import React from 'react';
import styled from 'styled-components';
import {AsideBodyScroll, AsideEmptyState, AsideFooter, AsideHeader, AsideSection, AsideWrapper} from './Aside';
import {useMainContentHeight} from '../../../page-structure/useMainContentHeight';

import {AsideRow} from './AsideRow';
import {ModalCloseBtn} from '../../elements/button/btn-type/ModalCloseBtn';

/**
 * @description
 * The <AsideSlideIn> component serves as a styled element for a slide in and slide out sidebar. Once positioned within your grid layout, you can integrate additional components to enrich the user interface design. Here are some examples of enhancements you can make:
 *
 * **AsideSlideIn Header**
 *
 * You can use the <AsideSlideIn.Header/> component to set a header title for the AsideSlideIn.
 *
 *  **AsideSlideIn Body**
 *
 * You can utilize the <AsideSlideIn.Body/> component to designate the main content section within the AsideSlideIn. This will also enable a scroll effect for the content within the Aside.Body.
 *
 *  **AsideSlideIn Section**
 *
 * The <AsideSlideIn.Section> component serves as the body of the <AsideSlideIn> component,
 * residing within the <AsideSlideIn.Body>. It is used to enclose content within the sidebar,
 * providing padding and separating different sections. You can utilize multiple <AsideSlideIn.Section> components within <AsideSlideIn.Body>
 * to organize and structure your content effectively.
 *
 *  **AsideSlideIn CloseBtn**
 *
 * This is a button component designed to display a close icon in gray color. When hovered over, it changes to red. This component is intended to be used within an `Aside` component as the close button for `Aside` modal components.
 * To use this, you should apply the component `<AsideSlideIn.CloseBtn onClick={onClose} />` inside the component `<AsideSlideIn.Header>`. If you are including a title in this header, make sure to place the button on the right side. You can view the example below to see how to do this.
 *
 * @param {object} param0
 *
 * @param {'sm' | 'lg' | 'xl'} [param0.size]
 * The size prop adjusts the width of the AsideSlideIn component.
 * Available options:
 * - 'sm': for a thinner width
 * - 'lg': for a wider width
 *
 *
 * @example
 * **Aside with sliding animation**
 *
 *  ```jsx
 * function YouCustomPage() {
 *      const {openSidebar, closeSidebar} = useSidebarContext();
 *      return (
 *          <>
 *              <Page>
 *                  <Main>
 *                      Main Page content:
 *                      <Btn color="success" subtle onClick={openSidebar}> <-- Look here!
 *                          {Button to show Aside component}
 *                      </Btn>
 *                  </Main>
 *                  <AsideSlideIn size="xl">
 *                      <AsideSlideIn.Header>
 *                          AsideSlideIn header
 *                          <Btn link subtle color="danger" onClick={closeSidebar}> // <-- Look here!
 *                              {Btn to hide Aside component}
 *                          </Btn>
 *                      </AsideSlideIn.Header>
 *                      <AsideSlideIn.Row>Your Aside content</AsideSlideIn.Row>
 *                  </AsideSlideIn>
 *              </Page>
 *          </>
 *      );
 * }
 * export default YouCustomPage;
 * ```
 */
export const AsideSlideIn = ({children, size}) => {
    const {targetRef} = useMainContentHeight();
    return (
        <AsideSlideInWrapper ref={targetRef} className={size}>
            {children}
        </AsideSlideInWrapper>
    );
};

export const AsideSlideInWrapper = styled(AsideWrapper)``;

/**
 * @description
 *
 * This is a button component designed to display a close icon in gray color. When hovered over, it changes to red. This component is intended to be used within an `Aside` component as the close button for `Aside` modal components.
 * To use this, you should apply the component <AsideSlideIn.CloseBtn onClick={onClose} /> inside the component <AsideSlideIn.Header>. If you are including a title in this header, make sure to place the button on the right side. You can view the example below to see how to do this."
 *
 * @param {object} param0
 *
 * @param {Function} [param0.onClick]
 * Callback function triggered when the button is clicked.
 *
 * @example
 *  ```jsx
 *
 *  <AsideSlideIn.Header>
 *      Header title
 *      <AsideSlideIn.CloseBtn onClick={onClose} />
 *  </AsideSlideIn.Header>
 * ```
 */
AsideSlideIn.CloseBtn = ModalCloseBtn;
AsideSlideIn.Wrapper = AsideSlideInWrapper;
AsideSlideIn.Header = AsideHeader;
AsideSlideIn.Body = AsideBodyScroll;
AsideSlideIn.Row = AsideRow;
AsideSlideIn.Footer = AsideFooter;
AsideSlideIn.Section = AsideSection;
AsideSlideIn.EmptyState = AsideEmptyState;
